/**
 * Help topic viewer page.
 * Dynamically loads a language-specific content component based on
 * the URL slug and the current locale.
 *
 * @module pages/help/HelpTopic
 */

import React, { Suspense, lazy, useMemo } from 'react';
import { useParams, Link }                from 'react-router-dom';
import { ArrowLeft }                      from 'lucide-react';
import Layout                             from '../../components/Layout';
import { useI18n }                        from '../../dataProviders/I18nProvider';
import config                             from '../../config';
import helpTopicRegistry                  from './helpTopicRegistry';

/**
 * Renders the help topic viewer with lazy-loaded content.
 *
 * @returns {React.ReactElement}
 */
export default function HelpTopic() {
  const { topicSlug }    = useParams();
  const { t, locale }    = useI18n();

  const topic = helpTopicRegistry.find((entry) => entry.slug === topicSlug);

  const ContentComponent = useMemo(() => {
    if (!topic) return null;
    const loader = topic.load[locale] || topic.load['en'];
    if (!loader) return null;
    return lazy(loader);
  }, [topic, locale]);

  if (!topic || !ContentComponent) {
    return (
      <Layout title={t('help.topicNotFound.title')}>
        <div className="help-article">
          <Link to={config.pagePaths.help} className="help-breadcrumb">
            <ArrowLeft size={16} />
            {t('help.backToIndex')}
          </Link>
          <p>{t('help.topicNotFound.message')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={t(topic.titleKey)}>
      <div className="help-article">
        <Link to={config.pagePaths.help} className="help-breadcrumb">
          <ArrowLeft size={16} />
          {t('help.backToIndex')}
        </Link>
        <Suspense fallback={<p className="help-loading">{t('help.loading')}</p>}>
          <ContentComponent />
        </Suspense>
      </div>
    </Layout>
  );
}
